import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import type { LeadCallout } from '../../types/leads'
import type { DraftView } from '../../types/voice'
import { Correct } from '../../components/Correct'

// What the lead page puts at the top: the PLAY first, then the states that raised it.
//
// A play card is a recommendation a person can act on without reading anything else — the play's name, the
// state that earned it in words, the methodology's own how-to, and a line to open with. The opener arrives
// with the page: it is a template in the tactic's words around facts already on the pursuit, so it costs no
// model call and asserts nothing about anybody. Pressing "Draft in my voice" is where a model enters, and it
// is handed the opener as the brief so the draft starts from the play rather than from a blank box.

export function CalloutCard({ c, onPerson, onPlay, modelConfigured = false }: { c: LeadCallout; onPerson: (id: string) => void; onPlay: (tacticId: string) => void; modelConfigured?: boolean }) {
  if (c.kind === 'tactic-due' && c.tacticId) return <PlayCard c={c} onPlay={onPlay} modelConfigured={modelConfigured} />
  return (
    <li className={`callout ${c.severity}`}>
      <strong>{c.title}</strong>
      <span>{c.detail}</span>
      {c.personId && <button className="link-button small" onClick={() => onPerson(c.personId!)}>Show on the map</button>}
    </li>
  )
}

export function PlayCard({ c, onPlay, modelConfigured = false }: { c: LeadCallout; onPlay: (tacticId: string) => void; modelConfigured?: boolean }) {
  return (
    <li className="callout play play-card">
      <strong>{c.tacticName ?? c.title}</strong>
      {c.why && <span className="play-why">Because {c.why}.</span>}
      <span className="play-how">{c.detail}</span>
      {c.opener && <p className="play-opener">{c.opener}</p>}
      <div className="play-actions">
        <button className="button small" onClick={() => onPlay(c.tacticId!)}>Run this play</button>
        <DraftInMyVoice c={c} modelConfigured={modelConfigured} />
      </div>
      {/* Scout chose this play from the playbook off the state named above. If that was the wrong call, say so
          here and the play the person names is the one planned — and the trigger is what the loop re-points. */}
      {c.suggestionId && (
        <Correct kind="play" entityId={c.suggestionId} entityLabel={c.tacticName ?? c.title}
          currentValue={`tactic:${c.tacticId}`} label="Not the right play?" />
      )}
    </li>
  )
}

/// The model path, asked for by pressing it. The brief IS the opener, so what comes back is this play in the
/// person's own voice rather than a fresh guess at what the mail should say.
function DraftInMyVoice({ c, modelConfigured }: { c: LeadCallout; modelConfigured: boolean }) {
  const spoke = useSpoke()
  const [draft, setDraft] = useState<DraftView | null>(null)
  const write = useMutation({
    mutationFn: () => voiceApi.draft(spoke, null, c.opener ?? `${c.tacticName}: ${c.detail}`, null),
    onSuccess: (d) => setDraft(d),
  })
  if (!c.opener && !c.detail) return null
  return (
    <>
      <button className="button small" onClick={() => write.mutate()} disabled={write.isPending || !modelConfigured}
        title={modelConfigured ? 'A draft in your own voice, written from this play and the pursuit.' : 'Drafting needs the CLI controller or an API model.'}>
        {write.isPending ? 'Writing…' : 'Draft in my voice'}
      </button>
      {write.isError && <span className="error small">{write.error instanceof RefusedError ? write.error.refusal.message : (write.error as Error).message}</span>}
      {draft && <textarea className="play-draft" rows={6} defaultValue={draft.draftText} aria-label="Draft" />}
    </>
  )
}
